import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CheckCircle, Eye, EyeSlash, Sparkle, Spinner, Warning } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Onboarding screen reached after a successful direct Stripe Checkout.
 * URL: /onboard?session_id=cs_xxx
 *
 * Flow:
 *   1. Look up the Stripe session via /api/checkout-session/{id}/preview
 *   2. Pre-fill the email so the user just picks a name + password
 *   3. POST /api/auth/register with the session_id so the backend links the
 *      paid subscription to the new account
 *   4. Send the user straight into /log with their subscription already active
 */
export default function Onboard() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id") || "";

  const [loadingSession, setLoadingSession] = useState(true);
  const [sessionState, setSessionState] = useState(null); // {payment_status, customer_email, plan, already_linked}
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!sessionId) {
        setError("Missing checkout session. If you just paid, please open the link from your receipt email.");
        setLoadingSession(false);
        return;
      }
      try {
        const { data } = await api.get(`/checkout-session/${sessionId}/preview`);
        if (!active) return;
        setSessionState(data);
        if (data.customer_email) setEmail(data.customer_email);
      } catch (e) {
        if (!active) return;
        setError(e?.response?.data?.detail || "Could not load your checkout session");
      } finally {
        if (active) setLoadingSession(false);
      }
    })();
    return () => { active = false; };
  }, [sessionId]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email || !password || password.length < 8) {
      toast.error("Email and a password of 8+ characters required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/register", {
        email,
        password,
        name: name || email.split("@")[0],
        checkout_session_id: sessionId,
      });
      await refresh();
      toast.success("Welcome to MedEasy — your subscription is active");
      navigate("/log", { replace: true });
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || "Could not finish registration";
      if (status === 409) {
        toast.error("That email already has a MedEasy account — please sign in instead.");
        navigate(`/login?next=/onboard&session_id=${sessionId}`);
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const paid = sessionState?.payment_status === "paid";
  const planLabel = "$49.50 / year";

  return (
    <div className="min-h-screen bg-[#FAF9F4] flex items-center justify-center px-5 py-10" data-testid="onboard-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-6 text-[#2C4C3B] hover:opacity-80">
          <img src="/medeasy-logo.png" alt="MedEasy" className="w-8 h-8 rounded-lg" />
          <span className="font-[Manrope] font-bold text-lg">MedEasy</span>
        </Link>

        {loadingSession ? (
          <Card className="rounded-3xl border-[#E3E1D9]">
            <CardContent className="p-10 flex flex-col items-center gap-3 text-[#5C6660]">
              <Spinner size={28} className="animate-spin text-[#2C4C3B]" />
              <div className="text-sm">Loading your checkout session…</div>
            </CardContent>
          </Card>
        ) : error ? (
          <Card className="rounded-3xl border-[#E3E1D9]">
            <CardContent className="p-8 text-center" data-testid="onboard-error">
              <div className="w-12 h-12 rounded-full bg-[#C17767]/15 flex items-center justify-center mx-auto">
                <Warning size={22} weight="duotone" className="text-[#C17767]" />
              </div>
              <h1 className="font-[Manrope] font-bold text-xl mt-4 text-[#1E2420]">We couldn&apos;t verify your payment</h1>
              <p className="text-sm text-[#5C6660] mt-2 leading-relaxed">{error}</p>
              <div className="mt-6 flex flex-col gap-2">
                <Button onClick={() => navigate("/subscribe")} className="rounded-full bg-[#2C4C3B] hover:bg-[#243D2F] text-white">Try checkout again</Button>
                <Button onClick={() => navigate("/login")} variant="outline" className="rounded-full">I already have an account</Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-3xl border-[#E3E1D9]" data-testid="onboard-form-card">
            <CardContent className="p-8">
              {paid ? (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2C4C3B]/8 text-[#2C4C3B] text-[11px] uppercase tracking-[0.22em] font-bold" data-testid="onboard-paid-badge">
                  <CheckCircle size={14} weight="fill" /> Payment received · {planLabel}
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4A373]/20 text-[#9C6B3F] text-[11px] uppercase tracking-[0.22em] font-bold" data-testid="onboard-unpaid-badge">
                  <Sparkle size={14} weight="fill" /> Pending payment · {planLabel}
                </div>
              )}

              <h1 className="font-[Manrope] font-bold text-3xl mt-4 text-[#1E2420] leading-tight">
                Finish your account
              </h1>
              <p className="text-sm text-[#5C6660] mt-2 leading-relaxed">
                {paid
                  ? "Your subscription is paid — just pick a password and you're in."
                  : "Looks like the payment isn't confirmed yet. Create your account now and we'll link it as soon as Stripe finalizes."}
              </p>

              <form className="mt-6 space-y-4" onSubmit={submit}>
                <div>
                  <Label htmlFor="onboard-name" className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">Your name</Label>
                  <Input
                    id="onboard-name"
                    data-testid="onboard-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Jamie L."
                    className="mt-2 h-12 rounded-xl border-[#E3E1D9]"
                  />
                </div>

                <div>
                  <Label htmlFor="onboard-email" className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">Email</Label>
                  <Input
                    id="onboard-email"
                    data-testid="onboard-email-input"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-2 h-12 rounded-xl border-[#E3E1D9]"
                    required
                  />
                  {sessionState?.customer_email && (
                    <p className="text-[11px] text-[#5C6660] mt-1.5">Pre-filled from your Stripe receipt</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="onboard-password" className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">Choose a password</Label>
                  <div className="relative mt-2">
                    <Input
                      id="onboard-password"
                      data-testid="onboard-password-input"
                      type={showPwd ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      className="h-12 rounded-xl border-[#E3E1D9] pr-10"
                      required
                    />
                    <button
                      type="button"
                      data-testid="onboard-password-toggle"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5C6660] p-1"
                      aria-label={showPwd ? "Hide password" : "Show password"}
                    >
                      {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button
                  data-testid="onboard-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full h-12 rounded-full bg-[#2C4C3B] hover:bg-[#243D2F] text-white text-base font-semibold"
                >
                  {busy ? "Creating your account…" : "Start tracking →"}
                </Button>

                <p className="text-xs text-[#5C6660] text-center leading-relaxed">
                  Already have a MedEasy account?{" "}
                  <Link to={`/login?next=/onboard&session_id=${sessionId}`} className="text-[#2C4C3B] underline font-semibold">
                    Sign in to link it
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        )}

        <p className="text-[11px] text-[#5C6660] mt-4 text-center">
          By creating an account you agree to MedEasy&apos;s{" "}
          <Link to="/terms" className="underline">Terms</Link> &amp;{" "}
          <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
